import React from 'react'
import { Link } from 'react-router'
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuLabel,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from '#app/components/ui/dropdown-menu.tsx'
import { Icon } from '#app/components/ui/icon.tsx'
import { cn } from '#app/lib/utils'
import {
	type ExplorePathParams,
	toExplorePath,
} from '../routes/explore_+/_to-path.ts'

type ParameterNames =
	| 'country'
	| 'region'
	| 'type'
	| 'trail'
	| 'capture'
	| 'frame'

type OptionsType = {
	country?: { name: string; path: string }[]
	region?: { name: string; path: string }[]
	type?: { name: string; path: string }[]
	trail?: { name: string; path: string }[]
	capture?: { name: string; path: string }[]
	frame?: { name: string; path: string }[]
}

const indexToParamName: { [key: number]: ParameterNames } = {
	0: 'country',
	1: 'region',
	2: 'type',
	3: 'trail',
	4: 'capture',
	5: 'frame',
}

interface BreadcrumbsProps {
	className?: string
	options: OptionsType
	country?: string
	region?: string
	type?: string
	trail?: string
	capture?: string
	frame?: string
}

const Breadcrumbs: React.FC<BreadcrumbsProps> = ({
	className,
	options,
	country,
	region,
	type,
	trail,
	capture,
	frame,
}) => {
	const segments: string[] = [
		country,
		region,
		type,
		trail,
		capture,
		frame,
	].filter((segment): segment is string => Boolean(segment))

	return (
		<nav className={cn(className, 'flex')} aria-label="Breadcrumb">
			<ol className="inline-flex flex-wrap items-center space-x-1 sm:space-x-2 md:space-x-3">
				<li className="inline-flex items-center">
					<Link
						to="/"
						className="inline-flex items-center text-xs font-medium sm:text-sm md:text-base"
					>
						<svg
							className="mr-1 h-3 w-3 sm:mr-2"
							xmlns="http://www.w3.org/2000/svg"
							fill="currentColor"
							viewBox="0 0 20 20"
						>
							<path d="m19.707 9.293-2-2-7-7a1 1 0 0 0-1.414 0l-7 7-2 2a1 1 0 0 0 1.414 1.414L2 10.414V18a2 2 0 0 0 2 2h3a1 1 0 0 0 1-1v-4a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1v4a1 1 0 0 0 1 1h3a2 2 0 0 0 2-2v-7.586l.293.293a1 1 0 0 0 1.414-1.414Z" />
						</svg>
						Home
					</Link>
				</li>
				<BreadcrumbLinks options={options} segments={segments} />
			</ol>
		</nav>
	)
}

interface BreadcrumbLinksProps {
	segments: string[]
	options: OptionsType
}

const BreadcrumbLinks: React.FC<BreadcrumbLinksProps> = ({
	segments,
	options,
}) => {
	return segments.map((segment, index) => {
		const crumbs = segments.slice(0, index + 1) as ExplorePathParams
		const explorePath = toExplorePath(...crumbs)
		const parameterName = indexToParamName[index]!
		const dropdownOptions: { name: string; path: string }[] | undefined =
			options[parameterName]
		const nextParameterName = indexToParamName[index + 1]!
		const nextDropdownOptions: { name: string; path: string }[] | undefined =
			options[nextParameterName]

		let dropDown = null
		if (dropdownOptions) {
			dropDown = (
				<DropdownMenu>
					<DropdownMenuTrigger className="ml-1 inline-flex items-center text-xs font-medium sm:text-sm md:text-base">
						<Icon name="chevron-down" className="h-3 w-3" />
					</DropdownMenuTrigger>
					<DropdownMenuContent className="z-1k">
						<DropdownMenuLabel className="text-sm">
							Select a {parameterName}
						</DropdownMenuLabel>
						<DropdownMenuSeparator />
						{dropdownOptions.map(({ name, path }) => {
							const optionSegments = [
								...crumbs.slice(0, -1),
								path,
							] as ExplorePathParams
							const exploreLink = toExplorePath(...optionSegments)
							return (
								<DropdownMenuItem key={exploreLink}>
									<Link
										to={exploreLink}
										className="block px-2 py-1 text-xs font-medium sm:text-sm md:text-base"
									>
										{name}
									</Link>
								</DropdownMenuItem>
							)
						})}
					</DropdownMenuContent>
				</DropdownMenu>
			)
		}

		let nextDropDown = null
		if (index === segments.length - 1 && nextDropdownOptions) {
			nextDropDown = (
				<DropdownMenu>
					<DropdownMenuTrigger className="ml-1 inline-flex items-center text-xs font-medium sm:text-sm md:text-base">
						<span>Select a {nextParameterName}</span>
						<Icon name="chevron-down" className="ml-1 h-3 w-3" />
					</DropdownMenuTrigger>
					<DropdownMenuContent className="z-1k">
						<DropdownMenuLabel className="text-sm">
							Select a {nextParameterName}
						</DropdownMenuLabel>
						<DropdownMenuSeparator />
						{nextDropdownOptions.map(({ name, path }) => {
							const nextOptionSegments = [
								...crumbs,
								path,
							] as unknown as ExplorePathParams
							const nextExploreLink = toExplorePath(...nextOptionSegments)
							return (
								<DropdownMenuItem key={nextExploreLink}>
									<Link
										to={nextExploreLink}
										className="block px-2 py-1 text-xs font-medium sm:text-sm md:text-base"
									>
										{name}
									</Link>
								</DropdownMenuItem>
							)
						})}
					</DropdownMenuContent>
				</DropdownMenu>
			)
		}

		return (
			<li key={explorePath} className="inline-flex items-center">
				<svg
					className="mx-1 h-3 w-3 sm:mx-2"
					aria-hidden="true"
					xmlns="http://www.w3.org/2000/svg"
					fill="none"
					viewBox="0 0 6 10"
				>
					<path
						stroke="currentColor"
						strokeLinecap="round"
						strokeLinejoin="round"
						strokeWidth="2"
						d="m1 9 4-4-4-4"
					/>
				</svg>
				{index === segments.length - 1 ? (
					<span className="text-xs font-medium sm:text-sm md:text-base">
						{segment}
					</span>
				) : (
					<Link
						to={explorePath}
						className="text-xs font-medium sm:text-sm md:text-base"
					>
						{segment}
					</Link>
				)}
				{dropDown}
				{nextDropDown}
			</li>
		)
	})
}

export default Breadcrumbs
